import { useEffect, useState } from 'react';
import { Box, Button, Group, Loader, Stack, Switch, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { PaymentAccount } from '../types/payment-account.type';

const client = new HttpClient<PaymentAccount>('/payment-account');

const PaymentAccountEdit: React.FC<{ id?: number }> = ({ id }) => {
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (!id) return;
    client.getById(id).then((res) => {
      const a = (res as any).data as PaymentAccount;
      if (a) {
        setName(a.name ?? '');
        setIban(a.iban ?? '');
        setBic(a.bic ?? '');
        setIsDefault(a.isDefault ?? false);
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!name || !iban) {
      notifications.show({ color: 'red', message: 'Bitte Bezeichnung und IBAN ausfüllen.' });
      return;
    }
    setSaving(true);
    try {
      const payload: any = { id: id ?? 0, name, iban, bic: bic || undefined, isDefault };
      const res: any = await client.post(payload);
      notifications.show({ color: 'green', message: 'Gespeichert.' });
      if (isNew) {
        navigate(`/payment-account/edit/${res?.data?.id ?? ''}`);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Speichern.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Zahlungskonto löschen?')) return;
    try {
      const res = await client.delete(id);
      if (res.status !== 200) throw new Error();
      navigate('/admin');
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Löschen.' });
    }
  };

  if (loading) return <Loader mt="xl" />;

  return (
    <Stack p="md" gap="md" pb={120}>
      <Title order={2}>{isNew ? 'Neues Zahlungskonto' : 'Zahlungskonto'}</Title>

      <TextInput label="Bezeichnung" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
      <TextInput label="IBAN" value={iban} onChange={(e) => setIban(e.currentTarget.value)} required />
      <TextInput label="BIC" value={bic} onChange={(e) => setBic(e.currentTarget.value)} />
      <Switch
        label="Standardkonto"
        description="Wird für Personen ohne Vertrag als Zahlungskonto verwendet"
        checked={isDefault}
        onChange={(e) => setIsDefault(e.currentTarget.checked)}
      />

      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--mantine-color-body)',
          padding: '12px 0',
          borderTop: '1px solid var(--mantine-color-default-border)',
          zIndex: 100,
        }}
      >
        <Group gap="sm" wrap="wrap">
          <Button onClick={handleSave} loading={saving} size="md" leftSection={<IconPlus size={16} />}>
            Speichern
          </Button>
          {!isNew && (
            <Button color="red" variant="light" onClick={handleDelete} size="md">
              Löschen
            </Button>
          )}
          <Button variant="subtle" onClick={() => navigate('/admin')} size="md">
            Zurück
          </Button>
        </Group>
      </Box>
    </Stack>
  );
};

export default PaymentAccountEdit;
